/**
 * Bolha de mensagem para exibição de botão PIX
 * 
 * Layout idêntico ao WhatsApp Web:
 * - Ícone PIX (logomarca oficial)
 * - Título "Pix" em negrito
 * - Subtítulo com tipo e chave formatada
 * - Botão "Copiar chave Pix" separado por borda
 * 
 * Segue padrão visual do ContactMessageBubble
 */

import { Copy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PixIcon } from '@/components/icons/PixIcon';

// Tipos de chave PIX
type PixKeyType = 'CPF' | 'CNPJ' | 'EMAIL' | 'CELULAR' | 'ALEATORIA';

interface PixButtonMessageBubbleProps {
  /** Nome do recebedor (mantido para compatibilidade, não exibido no WhatsApp) */
  name: string;
  /** Chave PIX */
  pixKey: string;
  /** Tipo da chave (CPF, CNPJ, Email, etc.) */
  keyType: PixKeyType;
  /** Mensagem opcional que acompanha o PIX */
  message?: string;
  /** Se a mensagem é do remetente (outbound) - afeta posicionamento */
  isOutbound?: boolean;
  /** Classes CSS adicionais */
  className?: string;
}

/**
 * Formata a chave PIX conforme seu tipo (sem mascaramento)
 * WhatsApp exibe a chave completa formatada
 */
function formatPixKey(key: string, keyType: PixKeyType): string {
  if (!key) return '';
  
  // Remove caracteres não numéricos para formatação
  const digits = key.replace(/\D/g, '');
  
  switch (keyType) {
    case 'CPF':
      // 12345678909 → 123.456.789-09
      if (digits.length === 11) {
        return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      }
      return key;
      
    case 'CNPJ':
      // 12345678000190 → 12.345.678/0001-90
      if (digits.length === 14) {
        return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      }
      return key;
      
    case 'CELULAR':
      // 11999999999 → (11) 99999-9999
      if (digits.length === 11) {
        return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
      }
      // 1199999999 → (11) 9999-9999 (fixo)
      if (digits.length === 10) {
        return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
      }
      return key;
      
    case 'EMAIL':
    case 'ALEATORIA':
    default:
      return key;
  }
}

export function PixButtonMessageBubble({
  name,
  pixKey,
  keyType,
  message,
  isOutbound = true,
  className,
}: PixButtonMessageBubbleProps) {
  // Chave formatada para exibição
  const formattedKey = formatPixKey(pixKey, keyType);

  return (
    <div
      className={cn('flex flex-col rounded-lg overflow-hidden max-w-[220px]', className)}
      style={{ 
        backgroundColor: '#FFFFFF',
        border: '1px solid #e9edef'
      }}
    >
      {/* Área principal com ícone e informações */}
      <div className="p-2 flex items-center gap-2">
        {/* Ícone PIX (logomarca oficial) */}
        <PixIcon className="h-10 w-10 flex-shrink-0 text-emerald-500" />

        {/* Informações - cores fixas WhatsApp */}
        <div className="flex-1 min-w-0">
          {/* Título "Pix" */}
          <p 
            className="text-xs font-semibold"
            style={{ color: '#111B21' }}
          >
            Pix
          </p>

          {/* Tipo e chave formatada */}
          <p 
            className="text-[10px] truncate"
            style={{ color: '#667781' }}
          >
            {keyType}: {formattedKey}
          </p>
        </div>
      </div>

      {/* Botão "Copiar chave Pix" - cor verde WhatsApp */}
      <div 
        className="px-2 py-1.5"
        style={{ borderTop: '1px solid #e9edef' }}
      >
        <div
          className="w-full flex items-center justify-center gap-1 cursor-default"
          style={{ color: '#00a884' }}
        >
          <Copy className="h-3 w-3" />
          <span className="text-[10px]">Copiar chave Pix</span>
        </div>
      </div>
    </div>
  );
}
